import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import { isMobile } from "react-device-detect";
import {
  UserAuthContextProvider,
  useUserAuth,
} from "./context/userAuthContext";
import { CurrencyProvider } from "./context/CurrencyContext.js";
import { CartProvider } from "./context/CartContext";
import NavigationBar from "./Navbar";
import MobileNavbar from "./MobileNavbar.js";
import PublicNavbar from "./PublicNavbar";
import AnalyticsTracker from "./AnalyticsTracker.js";

// Mantener todos los imports de componentes
import Home from "./components/Home";
import Login from "./components/Login";
import Signup from "./components/Signup";
import PhoneSignUp from "./components/PhoneSignUp";
import ProtectedRoute from "./components/ProtectedRoute";
import NotAuthorizedScreen from "./screens/NotAuthorizedScreen.jsx";
import UserMenu from "./components/Users/UserMenu";
import UserForm from "./components/Users/UserForm.js";
import ControlPanel from "./components/AdminPanel/ControlPanel";
import Profile from "./components/Profile/Profile";
import SearchFriends from "./components/Friends/SearchFriends";
import FriendRequests from "./components/Users/FriendRequests";
import SearchPanel from "./components/Search/SearchPanel";
import CheckOut from "./components/CheckOut/CheckOut.js";
import Success from "./components/CheckOut/Success";
import Failure from "./components/CheckOut/Failure";
import Pending from "./components/CheckOut/Pending";
import CreateStore from "./components/Stores/CreateStore.js";
import AllStores from "./components/Stores/AllStores.js";
import NewUser from "./components/Users/NewUser.js";
import Wallet from "./components/SpeedCoins/Wallet.js";
import TransactionDetails from "./components/SpeedCoins/TransactionDetails.js";
import MyFriends from "./components/Users/MyFriends.js";
import SendNotification from "./components/SendNotification.js";
import ProductDataFetcher from "./components/store/ProductDataFetcher.js";
import Categorias from "./components/store/categorias/Categorias.js";
import ProductosCategoria from "./components/store/categorias/ProductosCategoria.js";
import Search from "./components/store/productos/Search.js";
import Instaladores from "./components/MicroServicios/Instaladores.jsx";
import Servicios from "./components/MicroServicios/Servicios.jsx";
import Cart from "./components/store/carrito/Cart.jsx";
import MembershipView from "./components/Memberships/Membership.jsx";
import NewActivity from "./components/Activities/NewActivity";
import Inbox from "./components/Messenger/Inbox.jsx";
import ActivityInfo from "./components/Activities/ActivityInfo/ActivityInfo.jsx";
import MyActivities from "./components/Activities/MyActivities/MyActivities.jsx";
import NewCategory from "./components/Categories/NewCategory.jsx";
import Feed from "./components/Feed.jsx";
import Welcome from "./components/Home/Welcome.jsx";
import XFinanceSubscriptions from "./components/Finance/Subscriptions/XFinanceSubscriptions.jsx";
import XFinanceSubscriptionsList from "./components/Finance/Subscriptions/XFinanceSubscriptionsList.jsx";
import XFinanceSubscriptionForm from "./components/Finance/Subscriptions/XFinanceSubscriptionForm.jsx";
import XFinanceSubscriptionCategories from "./components/Finance/Subscriptions/XFinanceSubscriptionCategories.jsx";
import XFinanceDashboard from "./components/Finance/Dashboard/XFinanceDashboard.jsx";
import XFinanceExpensesList from "./components/Finance/Expenses/XFinanceExpensesList.jsx";
import XFinanceExpenseForm from "./components/Finance/Expenses/XFinanceExpenseForm.jsx";
import XFinanceExpenseCategories from "./components/Finance/Expenses/XFinanceExpenseCategories.jsx";
import XFinancePaymentMethods from "./components/Finance/Expenses/XFinancePaymentMethods.jsx";
import XFinanceIncomesList from "./components/Finance/Incomes/XFinanceIncomesList.jsx";
import XFinanceIncomeForm from "./components/Finance/Incomes/XFinanceIncomeForm.jsx";
import XFinanceIncomeCategories from "./components/Finance/Incomes/XFinanceIncomeCategories.jsx";
import XFinanceImportExpenses from "./components/Finance/Expenses/XFinanceImportExpenses.jsx";
import XFinanceImportData from "./components/Finance/Tools/XFinanceImportData.jsx";
import XFinanceConnect from "./components/Finance/Connect/XFinanceConnect.jsx";
import AboutSection from "./components/Home/AboutSection.jsx";
import HomeDashboard from "./components/Finance/Dashboard/HomeDashboard.jsx";
import MiguelPro from "./components/Finance/Cashback/MiguelPro.jsx";
import MultiEmailSender from "./components/Finance/Mail/MultiEmailSender.jsx";
import FormsList from "./components/Finance/Forms/FormsList.jsx";
import PoliciesDashboard from "./components/Finance/Policies/PoliciesDashboard.jsx";
import TravelInsuranceForm from "./components/Finance/Policies/TravelInsuranceForm.jsx";
import AutoInsuranceForm from "./components/Finance/Policies/AutoInsuranceForm.jsx";
import EditPolicy from "./components/Finance/Policies/EditPolicy.jsx";
import AdminPoliciesDashboard from "./components/Finance/Policies/AdminPoliciesDashboard.jsx";
import UserManagementPanel from "./components/Finance/Policies/UserManagementPanel.jsx";
import TempMakeAdmin from "./components/Finance/Policies/TempMakeAdmin.jsx";
import CardsDashboard from "./components/Finance/Cards/CardsDashboard.jsx";
import AmexCardApplicationForm from "./components/Finance/Cards/AmexCardApplicationForm.jsx";
import FinancialDashboard from "./components/FinancialDashboard.jsx";
import AdminDashboard from "./components/AdminDashboard.jsx";
import EventLeaderboard from "./components/EventLeaderboard.jsx";

// Componente AuthWrapper mejorado
const AuthWrapper = ({ children }) => {
  const { user, loading } = useUserAuth();
  const location = useLocation();

  const publicRoutes = [
    "/",
    "/login",
    "/signup",
    "/phonesignup",
    "/welcomeaboard",
    "/nosotros",
    "/dash",
    "/admindash",
    "/leaderboard",
  ];
  const isPublicRoute = publicRoutes.includes(location.pathname);

  // Rutas que NO deben mostrar el PublicNavbar
  const noNavbarRoutes = [
    "/login",
    "/signup",
    "/phonesignup",
    "/welcomeaboard",
  ];
  const shouldShowPublicNavbar =
    isPublicRoute && !noNavbarRoutes.includes(location.pathname);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#14840c] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Cargando Miguel.App...</p>
        </div>
      </div>
    );
  }

  if (!user && !isPublicRoute) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (user && isPublicRoute && location.pathname !== "/welcomeaboard") {
    return <Navigate to="/dashboard" replace />;
  }

  // Rutas públicas con PublicNavbar condicional
  if (isPublicRoute) {
    return (
      <div className="flex flex-col min-h-screen">
        {shouldShowPublicNavbar && <PublicNavbar />}
        <main className={`flex-1 ${shouldShowPublicNavbar ? "pt-16" : ""}`}>
          {children}
        </main>
      </div>
    );
  }

  // Rutas privadas con NavigationBar existente
  return (
    <div className="flex flex-col min-h-screen">
      <NavigationBar />
      <main className={`flex-1 ${isMobile ? "pt-16 pb-20" : "pt-16"}`}>
        {children}
      </main>
      <AnalyticsTracker />

      {isMobile && <MobileNavbar />}
    </div>
  );
};

// EnhancedProtectedRoute component
const EnhancedProtectedRoute = ({ children, role }) => {
  const { user, loading } = useUserAuth();
  const location = useLocation();
  const [userType, setUserType] = React.useState(null);
  const [checkingRole, setCheckingRole] = React.useState(true);

  // Load user type from Firestore if not in context
  React.useEffect(() => {
    const loadUserType = async () => {
      if (user?.uid && role) {
        try {
          const { doc, getDoc } = await import("firebase/firestore");
          const { db } = await import("./firebase");
          const userDoc = await getDoc(doc(db, "Users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserType(userData.type || null);
          } else {
            setUserType(user.type || null);
          }
        } catch (error) {
          console.error("Error loading user type:", error);
          setUserType(user.type || null);
        } finally {
          setCheckingRole(false);
        }
      } else {
        setUserType(user?.type || null);
        setCheckingRole(false);
      }
    };

    if (!loading) {
      loadUserType();
    }
  }, [user, role, loading]);

  if (loading || checkingRole) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-16 h-16 border-4 border-[#14840c] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Check role - allow access if role matches or if no role is required
  if (role) {
    const currentRole = userType || user.type;
    if (currentRole !== role) {
      // For admin routes, show a more helpful message
      if (role === "admin") {
        console.log("User type:", currentRole, "Required:", role);
      }
      return <Navigate to="/notauthorized" replace />;
    }
  }

  return children;
};

function App() {
  return (
    <UserAuthContextProvider>
      <CurrencyProvider>
        <CartProvider>
          <AuthWrapper>
            <Routes>
              {/* Rutas Públicas */}
              <Route path="/" element={<Welcome />} />
              <Route path="/login" element={<Login />} />
              <Route path="/nosotros" element={<AboutSection />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/phonesignup" element={<PhoneSignUp />} />
              <Route path="/welcomeaboard" element={<NewUser />} />
              <Route path="/leaderboard" element={<EventLeaderboard />} />

              <Route
                path="/producto/:documentName"
                element={
                  <EnhancedProtectedRoute>
                    <ProductDataFetcher />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/productos/categoria/:id"
                element={
                  <EnhancedProtectedRoute>
                    <ProductosCategoria />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/search/:input"
                element={
                  <EnhancedProtectedRoute>
                    <Search />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/categorias"
                element={
                  <EnhancedProtectedRoute>
                    <Categorias />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/sendNotification"
                element={
                  <EnhancedProtectedRoute>
                    <SendNotification />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/MyFriends/:uid"
                element={
                  <EnhancedProtectedRoute>
                    <MyFriends />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/transaction/:uid"
                element={
                  <EnhancedProtectedRoute>
                    <TransactionDetails />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/wallet"
                element={
                  <EnhancedProtectedRoute>
                    <Wallet />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/notauthorized"
                element={
                  <EnhancedProtectedRoute>
                    <NotAuthorizedScreen />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/search"
                element={
                  <EnhancedProtectedRoute>
                    <SearchPanel />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/friends"
                element={
                  <EnhancedProtectedRoute>
                    <SearchFriends />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/checkout/:uid"
                element={
                  <EnhancedProtectedRoute>
                    <CheckOut />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/geekdashboard"
                element={
                  <EnhancedProtectedRoute role="admin">
                    <ControlPanel />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/myactivities"
                element={
                  <EnhancedProtectedRoute>
                    <MyActivities />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/newactivity"
                element={
                  <EnhancedProtectedRoute>
                    <NewActivity />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/go/:uid"
                element={
                  <EnhancedProtectedRoute>
                    <ActivityInfo />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/memberships"
                element={
                  <EnhancedProtectedRoute>
                    <MembershipView />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/stores"
                element={
                  <EnhancedProtectedRoute>
                    <AllStores />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/createstore"
                element={
                  <EnhancedProtectedRoute>
                    <CreateStore />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/success/:orderId"
                element={
                  <EnhancedProtectedRoute>
                    <Success />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/failure"
                element={
                  <EnhancedProtectedRoute>
                    <Failure />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/pending"
                element={
                  <EnhancedProtectedRoute>
                    <Pending />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/friendsrequests"
                element={
                  <EnhancedProtectedRoute>
                    <FriendRequests />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/user/:uid"
                element={
                  <EnhancedProtectedRoute>
                    <Profile />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/addProfile"
                element={
                  <EnhancedProtectedRoute>
                    <UserForm />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <EnhancedProtectedRoute>
                    <UserMenu />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/instaladores"
                element={
                  <EnhancedProtectedRoute>
                    <Servicios />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/carrito"
                element={
                  <EnhancedProtectedRoute>
                    <Cart />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/messages"
                element={
                  <EnhancedProtectedRoute>
                    <Inbox />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/newcategory"
                element={
                  <EnhancedProtectedRoute>
                    <NewCategory />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <EnhancedProtectedRoute>
                    <XFinanceDashboard />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/subscriptions"
                element={
                  <EnhancedProtectedRoute>
                    <XFinanceSubscriptionsList />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/subscriptions/new"
                element={
                  <EnhancedProtectedRoute>
                    <XFinanceSubscriptionForm />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/subscriptions/edit/:id"
                element={
                  <EnhancedProtectedRoute>
                    <XFinanceSubscriptionForm />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/subscription-categories"
                element={
                  <EnhancedProtectedRoute>
                    <XFinanceSubscriptionCategories />
                  </EnhancedProtectedRoute>
                }
              />
              {/* Rutas para Gastos */}
              <Route
                path="/import/mixed"
                element={
                  <EnhancedProtectedRoute>
                    <XFinanceImportData />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/expenses"
                element={
                  <EnhancedProtectedRoute>
                    <XFinanceExpensesList />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/expenses/new"
                element={
                  <EnhancedProtectedRoute>
                    <XFinanceExpenseForm />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/expenses/edit/:id"
                element={
                  <EnhancedProtectedRoute>
                    <XFinanceExpenseForm />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/expense-categories"
                element={
                  <EnhancedProtectedRoute>
                    <XFinanceExpenseCategories />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/payment-methods"
                element={
                  <EnhancedProtectedRoute>
                    <XFinancePaymentMethods />
                  </EnhancedProtectedRoute>
                }
              />
              {/* Rutas para Ingresos */}
              <Route
                path="/incomes"
                element={
                  <EnhancedProtectedRoute>
                    <XFinanceIncomesList />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/incomes/new"
                element={
                  <EnhancedProtectedRoute>
                    <XFinanceIncomeForm />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/incomes/edit/:id"
                element={
                  <EnhancedProtectedRoute>
                    <XFinanceIncomeForm />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/income-categories"
                element={
                  <EnhancedProtectedRoute>
                    <XFinanceIncomeCategories />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/apps"
                element={
                  <EnhancedProtectedRoute>
                    <XFinanceConnect />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/home"
                element={
                  <EnhancedProtectedRoute>
                    <HomeDashboard />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/cashback"
                element={
                  <EnhancedProtectedRoute>
                    <MiguelPro />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/mails"
                element={
                  <EnhancedProtectedRoute>
                    <MultiEmailSender />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/formslist"
                element={
                  <EnhancedProtectedRoute>
                    <FormsList />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/polizas"
                element={
                  <EnhancedProtectedRoute>
                    <PoliciesDashboard />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/polizas/seguro-viajero"
                element={
                  <EnhancedProtectedRoute>
                    <TravelInsuranceForm />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/polizas/seguro-auto"
                element={
                  <EnhancedProtectedRoute>
                    <AutoInsuranceForm />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/polizas/edit/:policyId"
                element={
                  <EnhancedProtectedRoute>
                    <EditPolicy />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/polizas/admin"
                element={
                  <EnhancedProtectedRoute>
                    <AdminPoliciesDashboard />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/polizas/users"
                element={
                  <EnhancedProtectedRoute>
                    <UserManagementPanel />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/temp-admin"
                element={
                  <EnhancedProtectedRoute>
                    <TempMakeAdmin />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/cards"
                element={
                  <EnhancedProtectedRoute>
                    <CardsDashboard />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/cards/amex-application"
                element={
                  <EnhancedProtectedRoute>
                    <AmexCardApplicationForm />
                  </EnhancedProtectedRoute>
                }
              />
              <Route path="/dash" element={<FinancialDashboard />} />
              <Route path="/admindash" element={<AdminDashboard />} />
              {/* Ruta para manejar rutas no encontradas */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </AuthWrapper>
        </CartProvider>
      </CurrencyProvider>
    </UserAuthContextProvider>
  );
}

export default App;
